/**
 * Incident Model - Phase 13
 * 
 * Normalized incident event shape for merchant audit trail.
 * Aggregates data from queue items, sync logs, and recovery events.
 */

import { generateUUID } from '../offline/db';

// ==================== INCIDENT TYPES ====================

/**
 * Incident source types
 */
export const SOURCE_TYPES = {
  STAFF: 'staff',
  BACKOFFICE: 'backoffice',
  POS: 'pos',
  SYSTEM: 'system'
};

/**
 * Incident domains
 */
export const DOMAINS = {
  TIMECLOCK: 'timeclock',
  AVAILABILITY: 'availability',
  SCHEDULE: 'schedule',
  SETTINGS: 'settings',
  ORDERS: 'orders',
  PAYMENTS: 'payments',
  KITCHEN: 'kitchen',
  RECEIPTS: 'receipts',
  SYNC: 'sync',
  AUTH: 'auth',
  INVENTORY: 'inventory'
};

/**
 * Incident severity levels
 */
export const SEVERITY = {
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error',
  CRITICAL: 'critical'
};

/**
 * Incident status values
 */
export const INCIDENT_STATUS = {
  OBSERVED: 'observed',
  QUEUED: 'queued',
  RETRYING: 'retrying',
  RESOLVED: 'resolved',
  FAILED: 'failed',
  CONFLICT: 'conflict',
  MANUAL_REVIEW: 'manual_review',
  DISMISSED: 'dismissed'
};

/**
 * Follow-up status for manager workflow
 */
export const FOLLOWUP_STATUS = {
  NONE: 'none',
  REVIEWED: 'reviewed',
  NEEDS_FOLLOWUP: 'needs_followup',
  ESCALATED: 'escalated',
  DISMISSED: 'dismissed',
  RESOLVED_EXTERNALLY: 'resolved_externally'
};

// ==================== INCIDENT SHAPE ====================

/**
 * @typedef {Object} IncidentActor
 * @property {string|null} userId
 * @property {string|null} name
 * @property {string|null} role
 */

/**
 * @typedef {Object} Incident
 * @property {string} incidentId - Unique identifier
 * @property {string} sourceType - staff | backoffice | pos | system
 * @property {string} domain - timeclock | availability | orders | payments | etc.
 * @property {string} eventType - Original action type
 * @property {string} severity - info | warning | error | critical
 * @property {string} status - observed | queued | retrying | resolved | failed | conflict | manual_review | dismissed
 * @property {string} title - Human-readable title
 * @property {string} message - Detailed message
 * @property {IncidentActor} actor - Who triggered the event
 * @property {string|null} merchantId
 * @property {string|null} locationId
 * @property {string|null} terminalId
 * @property {string|null} deviceId
 * @property {string|null} localOrderId
 * @property {string|null} serverOrderId
 * @property {string|null} paymentIntentId
 * @property {string|null} queueItemId
 * @property {string|null} relatedEntityType
 * @property {string|null} relatedEntityId
 * @property {number} createdAt - When incident was created
 * @property {number|null} updatedAt
 * @property {number} occurredAt - When the underlying event occurred
 * @property {number|null} resolvedAt
 * @property {string|null} managerNote
 * @property {string[]} tags
 * @property {Object} metadata
 * @property {string} followUpStatus - none | reviewed | needs_followup | escalated | dismissed | resolved_externally
 * @property {string|null} reviewedBy
 * @property {number|null} reviewedAt
 */

// ==================== NORMALIZERS ====================

/**
 * Build incident title from event data
 * @param {Object} event
 * @returns {string}
 */
export function buildIncidentTitle(event) {
  const typeLabels = {
    // Staff actions
    'TIMECLOCK_PUNCH': 'Time Clock Punch',
    'AVAILABILITY_UPDATE': 'Availability Update',
    'SCHEDULE_ACK': 'Schedule Acknowledgment',
    
    // BackOffice actions
    'BUSINESS_PROFILE_UPDATE': 'Business Profile Update',
    'LOCATION_HOURS_UPDATE': 'Operating Hours Update',
    'STAFF_PROFILE_UPDATE': 'Staff Profile Update',
    'MENU_ITEM_CREATE': 'Menu Item Created',
    'MENU_ITEM_UPDATE': 'Menu Item Updated',
    'TAX_RATE_UPDATE': 'Tax Rate Update',
    'PAYMENT_METHOD_UPDATE': 'Payment Method Update',
    
    // POS actions
    'POS_ORDER_CREATE': 'POS Order Created',
    'POS_ORDER_UPDATE': 'POS Order Updated',
    'POS_ORDER_CLOSE': 'POS Order Closed',
    'POS_PAYMENT_CASH': 'Cash Payment',
    'POS_PAYMENT_CARD': 'Card Payment',
    'POS_KITCHEN_SEND': 'Kitchen Ticket Sent',
    'POS_SHIFT_OPEN': 'Shift Opened',
    'POS_SHIFT_CLOSE': 'Shift Closed',
    'POS_CASH_DROP': 'Cash Drop'
  };
  
  const label = typeLabels[event.type] || event.type?.replace(/_/g, ' ') || 'Unknown Event';
  
  // Add status context
  if (event.status === 'failed') {
    return `${label} - Failed`;
  }
  if (event.status === 'conflict') {
    return `${label} - Conflict Detected`;
  }
  
  return label;
}

/**
 * Compute incident severity from event data
 * @param {Object} event
 * @returns {string}
 */
export function buildIncidentSeverity(event) {
  // Critical conditions
  if (event.status === 'conflict' && event.type?.includes('PAYMENT')) {
    return SEVERITY.CRITICAL;
  }
  
  if (event.retryCount >= 5) {
    return SEVERITY.ERROR;
  }
  
  // Error conditions
  if (event.status === 'failed') {
    return SEVERITY.ERROR;
  }
  
  if (event.status === 'conflict') {
    return SEVERITY.WARNING;
  }
  
  // Payment-related get higher severity
  if (event.type?.includes('PAYMENT') && event.status !== 'completed') {
    return SEVERITY.WARNING;
  }
  
  // Retrying items
  if (event.status === 'pending' && event.retryCount > 0) {
    return SEVERITY.WARNING;
  }
  
  // Default
  if (event.status === 'completed' || event.status === 'resolved') {
    return SEVERITY.INFO;
  }
  
  return SEVERITY.INFO;
}

/**
 * Compute incident status from queue item status
 * @param {Object} event
 * @returns {string}
 */
export function buildIncidentStatus(event) {
  const statusMap = {
    'pending': INCIDENT_STATUS.QUEUED,
    'syncing': INCIDENT_STATUS.RETRYING,
    'completed': INCIDENT_STATUS.RESOLVED,
    'failed': INCIDENT_STATUS.FAILED,
    'conflict': INCIDENT_STATUS.CONFLICT
  };
  
  // Check for manual review status
  if (event.manualResolutionStatus === 'needs_followup') {
    return INCIDENT_STATUS.MANUAL_REVIEW;
  }
  if (event.manualResolutionStatus === 'dismissed') {
    return INCIDENT_STATUS.DISMISSED;
  }
  
  return statusMap[event.status] || INCIDENT_STATUS.OBSERVED;
}

/**
 * Determine source type from event data
 * @param {Object} event
 * @returns {string}
 */
export function determineSourceType(event) {
  const type = event.type || '';
  
  if (type.startsWith('POS_')) {
    return SOURCE_TYPES.POS;
  }
  
  if (type.includes('TIMECLOCK') || type.includes('AVAILABILITY') || type.includes('SCHEDULE_ACK')) {
    return SOURCE_TYPES.STAFF;
  }
  
  if (type.includes('SYNC') || type.includes('SYSTEM')) {
    return SOURCE_TYPES.SYSTEM;
  }
  
  return SOURCE_TYPES.BACKOFFICE;
}

/**
 * Determine domain from event data
 * @param {Object} event
 * @returns {string}
 */
export function determineDomain(event) {
  const type = event.type || '';
  const entityType = event.entityType || '';
  
  // Staff domain
  if (type.includes('TIMECLOCK') || entityType === 'timeclock') {
    return DOMAINS.TIMECLOCK;
  }
  if (type.includes('AVAILABILITY') || entityType === 'availability') {
    return DOMAINS.AVAILABILITY;
  }
  if (type.includes('SCHEDULE') || entityType === 'schedule') {
    return DOMAINS.SCHEDULE;
  }
  
  // POS domain
  if (type.includes('ORDER') || entityType.includes('order')) {
    return DOMAINS.ORDERS;
  }
  if (type.includes('PAYMENT') || entityType.includes('payment')) {
    return DOMAINS.PAYMENTS;
  }
  if (type.includes('KITCHEN') || entityType === 'pos_kitchen') {
    return DOMAINS.KITCHEN;
  }
  if (type.includes('PRINT') || type.includes('RECEIPT')) {
    return DOMAINS.RECEIPTS;
  }
  
  // Settings/BackOffice domain
  if (type.includes('PROFILE') || type.includes('HOURS') || type.includes('SETTINGS') || 
      type.includes('CONFIG') || type.includes('PREFS')) {
    return DOMAINS.SETTINGS;
  }
  
  if (type.includes('INVENTORY') || entityType === 'inventory') {
    return DOMAINS.INVENTORY;
  }
  
  // Sync domain
  if (type.includes('SYNC')) {
    return DOMAINS.SYNC;
  }
  
  return DOMAINS.SYNC;
}

/**
 * Build incident tags from event data
 * @param {Object} event
 * @returns {string[]}
 */
export function buildIncidentTags(event) {
  const tags = [];
  
  // Status tags
  if (event.status === 'failed') tags.push('failed');
  if (event.status === 'conflict') tags.push('conflict');
  if (event.status === 'pending' && event.retryCount > 0) tags.push('retrying');
  
  // Payment tags
  if (event.type?.includes('PAYMENT')) tags.push('payment');
  if (event.type?.includes('CASH')) tags.push('cash');
  if (event.type?.includes('CARD')) tags.push('card');
  
  // Priority tags
  if (event.priority <= 2) tags.push('high-priority');
  if (event.retryCount >= 5) tags.push('max-retries');
  
  // Review tags
  if (event.manualResolutionStatus === 'needs_followup') tags.push('needs-followup');
  if (event.manualResolutionStatus === 'escalated') tags.push('escalated');
  if (event.managerNote) tags.push('has-note');
  
  // Offline tags
  if (event.clientGeneratedAt) tags.push('offline-origin');
  
  return tags;
}

/**
 * Normalize a queue item to incident format
 * @param {Object} queueItem
 * @returns {Incident}
 */
export function normalizeQueueItemToIncident(queueItem) {
  const now = Date.now();
  
  return {
    incidentId: `inc_${queueItem.id || generateUUID()}`,
    sourceType: determineSourceType(queueItem),
    domain: determineDomain(queueItem),
    eventType: queueItem.type || 'UNKNOWN',
    severity: buildIncidentSeverity(queueItem),
    status: buildIncidentStatus(queueItem),
    title: buildIncidentTitle(queueItem),
    message: queueItem.lastError || `${queueItem.type} action ${queueItem.status}`,
    
    actor: {
      userId: queueItem.userId || null,
      name: queueItem.payload?.actor_name || queueItem.payload?.staff_name || null,
      role: queueItem.payload?.actor_role || null
    },
    
    merchantId: queueItem.merchantId || queueItem.payload?.restaurant_id || null,
    locationId: queueItem.payload?.location_id || queueItem.payload?.branch_id || null,
    terminalId: queueItem.payload?.terminal_id || queueItem.terminalId || null,
    deviceId: queueItem.payload?.device_id || null,
    
    localOrderId: queueItem.payload?.local_order_id || null,
    serverOrderId: queueItem.payload?.server_order_id || queueItem.payload?.order_id || null,
    paymentIntentId: queueItem.payload?.payment_intent_id || null,
    
    queueItemId: queueItem.id,
    relatedEntityType: queueItem.entityType || null,
    relatedEntityId: queueItem.entityId || null,
    
    createdAt: queueItem.createdAt || now,
    updatedAt: queueItem.updatedAt || null,
    occurredAt: queueItem.clientGeneratedAt || queueItem.createdAt || now,
    resolvedAt: queueItem.status === 'completed' ? queueItem.updatedAt : null,
    
    managerNote: queueItem.managerNote || null,
    tags: buildIncidentTags(queueItem),
    metadata: {
      retryCount: queueItem.retryCount || 0,
      nextRetryAt: queueItem.nextRetryAt || null,
      idempotencyKey: queueItem.idempotencyKey || null,
      endpoint: queueItem.endpoint || null,
      method: queueItem.method || null,
      priority: queueItem.priority || 5,
      payload: sanitizePayload(queueItem.payload)
    },
    
    followUpStatus: queueItem.manualResolutionStatus || FOLLOWUP_STATUS.NONE,
    reviewedBy: queueItem.reviewedBy || null,
    reviewedAt: queueItem.reviewedAt || null
  };
}

/**
 * Normalize a sync log entry to incident format
 * @param {Object} logEntry
 * @returns {Incident}
 */
export function normalizeSyncLogToIncident(logEntry) {
  const now = Date.now();
  
  // Determine status from log result
  let status = INCIDENT_STATUS.OBSERVED;
  let severity = SEVERITY.INFO;
  
  if (logEntry.result === 'success') {
    status = INCIDENT_STATUS.RESOLVED;
  } else if (logEntry.result === 'failed') {
    status = INCIDENT_STATUS.FAILED;
    severity = SEVERITY.ERROR;
  } else if (logEntry.result === 'conflict') {
    status = INCIDENT_STATUS.CONFLICT;
    severity = SEVERITY.WARNING;
  } else if (logEntry.result === 'retry') {
    status = INCIDENT_STATUS.RETRYING;
    severity = SEVERITY.WARNING;
  }
  
  return {
    incidentId: `inc_log_${logEntry.id || generateUUID()}`,
    sourceType: SOURCE_TYPES.SYSTEM,
    domain: DOMAINS.SYNC,
    eventType: logEntry.type || 'SYNC_EVENT',
    severity,
    status,
    title: `Sync ${logEntry.result}: ${logEntry.type || 'Unknown'}`,
    message: logEntry.message || `Sync operation ${logEntry.result}`,
    
    actor: {
      userId: null,
      name: 'System',
      role: 'system'
    },
    
    merchantId: null,
    locationId: null,
    terminalId: null,
    deviceId: null,
    localOrderId: null,
    serverOrderId: null,
    paymentIntentId: null,
    
    queueItemId: logEntry.queueItemId || null,
    relatedEntityType: null,
    relatedEntityId: null,
    
    createdAt: logEntry.syncedAt || now,
    updatedAt: null,
    occurredAt: logEntry.syncedAt || now,
    resolvedAt: logEntry.result === 'success' ? logEntry.syncedAt : null,
    
    managerNote: null,
    tags: [logEntry.result, 'sync-log'],
    metadata: {
      statusCode: logEntry.statusCode || null,
      durationMs: logEntry.durationMs || null
    },
    
    followUpStatus: FOLLOWUP_STATUS.NONE,
    reviewedBy: null,
    reviewedAt: null
  };
}

/**
 * Normalize a recovery item to incident format
 * @param {Object} recoveryItem
 * @returns {Incident}
 */
export function normalizeRecoveryItemToIncident(recoveryItem) {
  const now = Date.now();
  
  return {
    incidentId: `inc_rec_${recoveryItem.id || generateUUID()}`,
    sourceType: determineSourceType(recoveryItem),
    domain: determineDomain(recoveryItem),
    eventType: recoveryItem.type || 'RECOVERY_EVENT',
    severity: buildIncidentSeverity(recoveryItem),
    status: buildIncidentStatus(recoveryItem),
    title: `Recovery: ${buildIncidentTitle(recoveryItem)}`,
    message: recoveryItem.lastError || recoveryItem.message || 'Recovery item',
    
    actor: {
      userId: recoveryItem.userId || null,
      name: recoveryItem.actorName || null,
      role: recoveryItem.actorRole || null
    },
    
    merchantId: recoveryItem.merchantId || null,
    locationId: recoveryItem.locationId || recoveryItem.branchId || null,
    terminalId: recoveryItem.terminalId || null,
    deviceId: recoveryItem.deviceId || null,
    localOrderId: recoveryItem.localOrderId || null,
    serverOrderId: recoveryItem.serverOrderId || null,
    paymentIntentId: recoveryItem.paymentIntentId || null,
    
    queueItemId: recoveryItem.queueItemId || recoveryItem.id,
    relatedEntityType: recoveryItem.entityType || null,
    relatedEntityId: recoveryItem.entityId || null,
    
    createdAt: recoveryItem.createdAt || now,
    updatedAt: recoveryItem.updatedAt || null,
    occurredAt: recoveryItem.occurredAt || recoveryItem.createdAt || now,
    resolvedAt: recoveryItem.resolvedAt || null,
    
    managerNote: recoveryItem.managerNote || null,
    tags: buildIncidentTags(recoveryItem),
    metadata: recoveryItem.metadata || {},
    
    followUpStatus: recoveryItem.followUpStatus || FOLLOWUP_STATUS.NONE,
    reviewedBy: recoveryItem.reviewedBy || null,
    reviewedAt: recoveryItem.reviewedAt || null
  };
}

// ==================== HELPERS ====================

/**
 * Sanitize payload for safe export (remove sensitive data)
 * @param {Object} payload
 * @returns {Object}
 */
function sanitizePayload(payload) {
  if (!payload) return {};
  
  const sanitized = { ...payload };
  
  // Remove sensitive fields
  const sensitiveFields = [
    'pin', 'password', 'token', 'secret', 'api_key', 'apiKey',
    'card_number', 'cardNumber', 'cvv', 'cvc', 'expiry',
    'ssn', 'social_security', 'bank_account', 'routing_number',
    'private_key', 'privateKey', 'credential'
  ];
  
  sensitiveFields.forEach(field => {
    if (sanitized[field]) {
      sanitized[field] = '[REDACTED]';
    }
  });
  
  // Recursively sanitize nested objects (1 level deep)
  Object.keys(sanitized).forEach(key => {
    if (sanitized[key] && typeof sanitized[key] === 'object' && !Array.isArray(sanitized[key])) {
      sensitiveFields.forEach(field => {
        if (sanitized[key][field]) {
          sanitized[key][field] = '[REDACTED]';
        }
      });
    }
  });
  
  return sanitized;
}

/**
 * Create empty incident template
 * @returns {Incident}
 */
export function createEmptyIncident() {
  return {
    incidentId: `inc_${generateUUID()}`,
    sourceType: SOURCE_TYPES.SYSTEM,
    domain: DOMAINS.SYNC,
    eventType: 'UNKNOWN',
    severity: SEVERITY.INFO,
    status: INCIDENT_STATUS.OBSERVED,
    title: '',
    message: '',
    actor: { userId: null, name: null, role: null },
    merchantId: null,
    locationId: null,
    terminalId: null,
    deviceId: null,
    localOrderId: null,
    serverOrderId: null,
    paymentIntentId: null,
    queueItemId: null,
    relatedEntityType: null,
    relatedEntityId: null,
    createdAt: Date.now(),
    updatedAt: null,
    occurredAt: Date.now(),
    resolvedAt: null,
    managerNote: null,
    tags: [],
    metadata: {},
    followUpStatus: FOLLOWUP_STATUS.NONE,
    reviewedBy: null,
    reviewedAt: null
  };
}

/**
 * Check if incident is open (needs attention)
 * @param {Incident} incident
 * @returns {boolean}
 */
export function isIncidentOpen(incident) {
  const openStatuses = [
    INCIDENT_STATUS.QUEUED,
    INCIDENT_STATUS.RETRYING,
    INCIDENT_STATUS.FAILED,
    INCIDENT_STATUS.CONFLICT,
    INCIDENT_STATUS.MANUAL_REVIEW
  ];
  
  return openStatuses.includes(incident.status);
}

/**
 * Check if incident is critical
 * @param {Incident} incident
 * @returns {boolean}
 */
export function isIncidentCritical(incident) {
  return incident.severity === SEVERITY.CRITICAL;
}

/**
 * Get display label for domain
 * @param {string} domain
 * @returns {string}
 */
export function getDomainDisplayLabel(domain) {
  const labels = {
    [DOMAINS.TIMECLOCK]: 'Time Clock',
    [DOMAINS.AVAILABILITY]: 'Availability',
    [DOMAINS.SCHEDULE]: 'Schedule',
    [DOMAINS.SETTINGS]: 'Settings',
    [DOMAINS.ORDERS]: 'Orders',
    [DOMAINS.PAYMENTS]: 'Payments',
    [DOMAINS.KITCHEN]: 'Kitchen',
    [DOMAINS.RECEIPTS]: 'Receipts',
    [DOMAINS.SYNC]: 'Sync',
    [DOMAINS.AUTH]: 'Authentication',
    [DOMAINS.INVENTORY]: 'Inventory'
  };
  
  return labels[domain] || domain;
}

/**
 * Get display label for source type
 * @param {string} sourceType
 * @returns {string}
 */
export function getSourceTypeDisplayLabel(sourceType) {
  const labels = {
    [SOURCE_TYPES.STAFF]: 'Staff Portal',
    [SOURCE_TYPES.BACKOFFICE]: 'Back Office',
    [SOURCE_TYPES.POS]: 'POS Terminal',
    [SOURCE_TYPES.SYSTEM]: 'System'
  };
  
  return labels[sourceType] || sourceType;
}

export default {
  // Types
  SOURCE_TYPES,
  DOMAINS,
  SEVERITY,
  INCIDENT_STATUS,
  FOLLOWUP_STATUS,
  
  // Builders
  buildIncidentTitle,
  buildIncidentSeverity,
  buildIncidentStatus,
  buildIncidentTags,
  
  // Normalizers
  normalizeQueueItemToIncident,
  normalizeSyncLogToIncident,
  normalizeRecoveryItemToIncident,
  
  // Helpers
  createEmptyIncident,
  isIncidentOpen,
  isIncidentCritical,
  getDomainDisplayLabel,
  getSourceTypeDisplayLabel,
  determineSourceType,
  determineDomain
};
